import type { ReactNode } from 'react';
import { cn } from '@/lib/cn';
import { Skeleton } from './Skeleton';

/**
 * DataTable — the admin and seller console workhorse.
 *
 * The admin mockups describe these screens as ones "an operator sits in for six
 * hours, not a marketing page", so this table optimises for density and for
 * scanning down a column: sticky headers, hairline row separators, and numeric
 * columns that are monospaced and right-aligned so ₦4,140 and ₦55,800 line up
 * on the decimal.
 *
 * It carries its own loading and empty states because a table that renders a
 * bare `<tbody>` while data loads causes the layout to jump the moment it
 * arrives — and an operator working a queue loses their place.
 */

export interface Column<Row> {
  key: string;
  header: ReactNode;
  /** Right-aligned and monospaced. Use for money and counts. */
  numeric?: boolean;
  width?: string;
  render: (row: Row) => ReactNode;
}

export interface DataTableProps<Row> {
  columns: Column<Row>[];
  rows: Row[];
  rowKey: (row: Row) => string | number;
  loading?: boolean;
  skeletonRows?: number;
  empty?: ReactNode;
  /** Highlight a row: an escalated dispute, a selected queue item. */
  rowTone?: (row: Row) => 'danger' | 'selected' | undefined;
  onRowClick?: (row: Row) => void;
  className?: string;
}

export function DataTable<Row>({
  columns,
  rows,
  rowKey,
  loading = false,
  skeletonRows = 6,
  empty,
  rowTone,
  onRowClick,
  className,
}: DataTableProps<Row>) {
  return (
    <div className={cn('w-full overflow-x-auto', className)}>
      <table className="w-full border-collapse text-[12.5px]">
        <thead>
          <tr>
            {columns.map((column) => (
              <th
                key={column.key}
                scope="col"
                style={column.width ? { width: column.width } : undefined}
                className={cn(
                  'sticky top-0 z-[2] border-b border-line bg-paper px-3 py-[9px]',
                  'text-[10.5px] font-bold uppercase tracking-[0.06em] text-faint',
                  column.numeric ? 'text-right' : 'text-left',
                )}
              >
                {column.header}
              </th>
            ))}
          </tr>
        </thead>

        <tbody>
          {loading
            ? Array.from({ length: skeletonRows }).map((_, index) => (
                <tr key={`skeleton-${index}`}>
                  {columns.map((column) => (
                    <td key={column.key} className="border-b border-line-2 px-3 py-[11px]">
                      <Skeleton className={cn('h-3', column.numeric ? 'ml-auto w-16' : 'w-3/4')} />
                    </td>
                  ))}
                </tr>
              ))
            : rows.map((row) => {
                const tone = rowTone?.(row);

                return (
                  <tr
                    key={rowKey(row)}
                    onClick={onRowClick ? () => onRowClick(row) : undefined}
                    className={cn(
                      'group',
                      onRowClick && 'cursor-pointer',
                      tone === 'danger' && 'bg-danger-tint',
                      tone === 'selected' && 'bg-blue-tint-2 shadow-[inset_3px_0_0_var(--color-blue)]',
                    )}
                  >
                    {columns.map((column) => (
                      <td
                        key={column.key}
                        className={cn(
                          'border-b border-line-2 px-3 py-[11px] align-middle text-ink',
                          'group-hover:bg-blue-tint-2',
                          tone === 'danger' && 'group-hover:bg-danger-tint',
                          column.numeric && 'text-right font-mono text-xs tabular',
                        )}
                      >
                        {column.render(row)}
                      </td>
                    ))}
                  </tr>
                );
              })}
        </tbody>
      </table>

      {!loading && rows.length === 0 ? (
        <div className="border-b border-line-2 px-3 py-10 text-center text-xs text-faint">
          {empty ?? 'Nothing here yet.'}
        </div>
      ) : null}
    </div>
  );
}
